import { allFullProducts, productActionLoading } from '../../../store/reducer/product.reducer';
import { deepCopy } from 'src/app/shared/constants-and-enums/default-objects';
import { fullProductById, selectedRegistrationPeriodId } from '../../../store/store';
import {
    AddUpdateRemoveCompanyProductSelection,
    LoadCompanyProductSelectionsByCompany
} from 'src/app/store/actions/company-product-selection.actions';
import {
    afterNextRender,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    viewChild
} from '@angular/core';
import {
    CompanyProductSelectionPricingOptionWithOverrideInformation,
    ProductPricingOptionDTO
} from '@iota/iota-management-portal-v4-client';
import { ModalService } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import { DeletePricingOption, UpdatePricingOptionsOrder, UpsertPricingOption } from 'src/app/store/actions/product.actions';
import { DefaultBudgetPricingOption, DefaultProductPricingOption } from '../../constants-and-enums/default-objects';
import { ConfirmationDialogData, DialogConfirmationComponent } from '../../dialogs/dialog-confirmation/dialog-confirmation.component';
import { getValueFromStore, waitForBooleanValue, waitForValueNotFalsy } from '../../utils';
import { ReloadRegistrationPeriod } from 'src/app/store/actions/registration-period.action';
import {
    companyProductSelectionActionLoading,
    companyProductSelectionLoading
} from 'src/app/store/reducer/company-product-selection.reducer';
import { CompanyProductSelectionWithMeta } from 'src/app/store/entity-models';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AsyncState } from '../../utils/async-state.util';
import { PriceScaleFormValue } from '../../utils/price-scale-intervals.util';
import { duplicatePricingOption as createPricingOptionDuplicate } from '../../utils/pricing-option-duplicate.util';
import _ from 'lodash';

@Component({
    standalone: false,
    selector: 'app-product-pricing-option-list',
    templateUrl: './product-pricing-option-list.component.html',
    styleUrls: ['./product-pricing-option-list.component.scss']
})
export class ProductPricingOptionListComponent implements OnDestroy, OnChanges {
    @Input() title = $localize`Budget-options`;
    @Input() companyProductSelection: CompanyProductSelectionWithMeta;
    @Input() companyId: string;
    readonly productId = input<string>();
    readonly priceScaleState = input.required<AsyncState<PriceScaleFormValue | null>>();

    sortedPricingOptions: (ProductPricingOptionDTO | CompanyProductSelectionPricingOptionWithOverrideInformation)[];
    sortOrderChanged = false;

    private readonly fullProducts = this.store.selectSignal(allFullProducts);
    readonly fullProduct = computed(() => this.fullProducts().find((product) => product.productId === this.productId()));

    private readonly listTop = viewChild.required<ElementRef<HTMLElement>>('listTop');
    private readonly injector = inject(Injector);

    get pricingOptionsToDisplay(): (ProductPricingOptionDTO | CompanyProductSelectionPricingOptionWithOverrideInformation)[] {
        if (this.companyId) {
            return this.sortedPricingOptions;
        }

        return (
            this.sortedPricingOptions
                ?.filter((option) => !option.companyId)
                .filter((option) => {
                    const isOverride =
                        (option as any).isOverride ?? (option as CompanyProductSelectionPricingOptionWithOverrideInformation).override;
                    if (isOverride || (option.companyId && option.productPricingOptionId)) {
                        return this.companyProductSelection?.additionalProductPricingOptionIds.includes(option.productPricingOptionId);
                    } else {
                        return true;
                    }
                }) || []
        );
    }

    constructor(
        private store: Store,
        private dialog: ModalService
    ) {
        // Only the store-backed branch is tracked here; the input-backed one is covered by ngOnChanges.
        effect(() => this.initPricingOptions());
    }

    ngOnChanges(changes: SimpleChanges): void {
        const prevCPS = changes?.companyProductSelection?.previousValue;
        const currentCPS = changes?.companyProductSelection?.currentValue;

        if (!_.isEqual(prevCPS, currentCPS) || !!changes?.companyId) {
            this.initPricingOptions();
        }
    }

    ngOnDestroy() {
        if (this.sortOrderChanged) {
            this.reloadCompanySelections(false);
        }
    }

    initPricingOptions(): void {
        if (this.companyId) {
            // Overwritten PricingOptions are displayed last
            this.sortedPricingOptions = [...(this.companyProductSelection?.pricingOptions ?? [])]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .sort((a, b) => (a.overwritten ? 1 : 0) - (b.overwritten ? 1 : 0));
        } else {
            this.sortedPricingOptions = [...(this.fullProduct()?.productPricingOptions ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
        }
    }

    isEditable(pricingOption: ProductPricingOptionDTO): boolean {
        return !this.companyId || pricingOption.companyId === this.companyId;
    }

    add() {
        const po = this.companyId ? DefaultBudgetPricingOption() : DefaultProductPricingOption();
        po.productId = this.productId();
        po.companyId = this.companyId || null;
        po.optionKey = 'BUDGET';
        this.sortedPricingOptions = [po].concat(this.sortedPricingOptions);
        this.scrollToTop();
    }

    duplicatePricingOption(source: ProductPricingOptionDTO | CompanyProductSelectionPricingOptionWithOverrideInformation) {
        const copy = createPricingOptionDuplicate(source, { productId: this.productId(), companyId: this.companyId || null });
        this.sortedPricingOptions = [copy].concat(this.sortedPricingOptions);
        this.scrollToTop();
    }

    deletePricingOption(pricingOption: ProductPricingOptionDTO) {
        const data: ConfirmationDialogData = {
            title: $localize`Delete budget-option?`,
            text: $localize`Are you sure you want to delete the budget-option?`,
            cancelButtonStyle: 'primary',
            confirmButtonStyle: 'error'
        };

        this.dialog
            .open(DialogConfirmationComponent, { data })
            .afterClosed()
            .subscribe(async (confirmed) => {
                if (confirmed) {
                    this.sortedPricingOptions = this.sortedPricingOptions.filter(
                        (p) => p.productPricingOptionId !== pricingOption.productPricingOptionId
                    );

                    if (pricingOption.productPricingOptionId) {
                        this.store.dispatch(
                            DeletePricingOption({
                                pricingOptionId: pricingOption.productPricingOptionId,
                                productId: pricingOption.productId
                            })
                        );
                        if (
                            this.companyProductSelection &&
                            this.companyProductSelection.additionalProductPricingOptionIds.includes(pricingOption.productPricingOptionId)
                        ) {
                            const selectionCopy = deepCopy(this.companyProductSelection);
                            //remove from additionalProductPricingOptionIds if it was an override
                            selectionCopy.additionalProductPricingOptionIds =
                                this.companyProductSelection.additionalProductPricingOptionIds.filter(
                                    (pricingId) => pricingId !== pricingOption.productPricingOptionId
                                );
                            this.companyProductSelection = selectionCopy;
                            this.store.dispatch(AddUpdateRemoveCompanyProductSelection({ upsertedProductSelections: [selectionCopy] }));
                            await this.reloadCompanySelections(true);
                        }
                    }
                }
            });
    }

    async save(pricingOption: ProductPricingOptionDTO, sortOrder: number) {
        const isNew = !pricingOption.productPricingOptionId;
        const previousPricingOptionIds = (this.fullProduct()?.productPricingOptions ?? []).map((option) => option.productPricingOptionId);

        this.store.dispatch(UpsertPricingOption({ pricingOption: { ...pricingOption, sortOrder: sortOrder } }));
        await waitForBooleanValue(this.store, productActionLoading, false);

        let newPricingOptionId: string | undefined;
        if (isNew) {
            const updatedProduct = await waitForValueNotFalsy(this.store, fullProductById(this.productId()));
            newPricingOptionId = updatedProduct.productPricingOptions
                .filter((option) => !previousPricingOptionIds.includes(option.productPricingOptionId))
                .map((option) => option.productPricingOptionId)[0];
        }

        const selectionUpsertDispatched = !!newPricingOptionId && !!this.companyProductSelection;
        if (selectionUpsertDispatched) {
            const selectionCopy = deepCopy(this.companyProductSelection);
            selectionCopy.additionalProductPricingOptionIds = [newPricingOptionId].concat(
                this.companyProductSelection.additionalProductPricingOptionIds
            );
            this.companyProductSelection = selectionCopy;
            this.store.dispatch(AddUpdateRemoveCompanyProductSelection({ upsertedProductSelections: [selectionCopy] }));
        }

        await this.reloadCompanySelections(selectionUpsertDispatched);
    }

    // A selection upsert reloads the selections itself; a second, concurrent load could read the state before the upsert.
    private async reloadCompanySelections(selectionUpsertDispatched: boolean): Promise<void> {
        if (!this.companyId) {
            return;
        }
        if (!selectionUpsertDispatched) {
            this.store.dispatch(LoadCompanyProductSelectionsByCompany({ companyId: this.companyId }));
        }

        await waitForBooleanValue(this.store, companyProductSelectionActionLoading, false);
        await waitForBooleanValue(this.store, companyProductSelectionLoading, false);

        const registrationPeriodId = await getValueFromStore(this.store, selectedRegistrationPeriodId);
        if (registrationPeriodId) {
            this.store.dispatch(ReloadRegistrationPeriod({ registrationPeriodId }));
        }
    }

    private scrollToTop(): void {
        afterNextRender(() => this.listTop().nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }), {
            injector: this.injector
        });
    }

    checkCancel(pricingOption: ProductPricingOptionDTO) {
        if (!pricingOption.productPricingOptionId) {
            this.sortedPricingOptions.splice(this.sortedPricingOptions.indexOf(pricingOption), 1);
        }
    }

    drop(event: CdkDragDrop<any[]>) {
        const sortedPricingOptions = [...this.pricingOptionsToDisplay];
        const pricingOptionsNotDisplay = this.sortedPricingOptions.filter(
            (element) =>
                !this.pricingOptionsToDisplay.find(
                    (displayElement) => displayElement.productPricingOptionId === element.productPricingOptionId
                )
        );

        moveItemInArray(sortedPricingOptions, event.previousIndex, event.currentIndex);

        sortedPricingOptions.push(...pricingOptionsNotDisplay);

        this.store.dispatch(
            UpdatePricingOptionsOrder({
                pricingOptions: [...sortedPricingOptions].map((option, idx) => ({ ...option, sortOrder: idx })),
                productId: sortedPricingOptions[0]?.productId
            })
        );
        this.sortOrderChanged = true;
    }
}
